import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { IntroNote } from "@/components/IntroNote";
import { Navbar } from "@/components/Navbar";
import { ShieldCheck, FileText, ArrowRight } from "lucide-react";

const STATUS_STYLE = {
  implemented: "bg-green-100 text-green-700",
  partial: "bg-amber-100 text-amber-700",
  planned: "bg-slate-100 text-slate-500",
};

const Pill = ({ s }) => (
  <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase ${STATUS_STYLE[s] || ""}`}>{s}</span>
);

export default function ComplianceReadiness() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/compliance/readiness").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const s = data?.summary;

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-8" data-testid="compliance-readiness-page">
        <Seo title="Compliance readiness" description="SOC 2 / ISO 27001 control matrix, readiness score and gap analysis." path="/compliance-readiness" />
        <p className="ek-eyebrow">Owner · Trust &amp; Compliance</p>
        <h1 className="ek-heading mt-1 text-3xl">SOC 2 / ISO 27001 readiness</h1>

        <IntroNote>
          A live, honest self-assessment of ERIO's control environment mapped to SOC 2 Trust Services
          Criteria and ISO/IEC 27001:2022. Certification is issued by an independent auditor — this
          evidences readiness and the path to it. The full prep pack (control matrix, gap analysis,
          policy set, evidence register) is in the Trust Centre.
        </IntroNote>

        {!data ? (
          <p className="text-sm text-ek-ink/65">Loading…</p>
        ) : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="readiness-summary">
              <div className="rounded-xl border border-ek-purple/15 bg-ek-purple/[0.05] p-4">
                <p className="text-xs uppercase tracking-wide text-ek-purple/70">Readiness</p>
                <p className="font-display text-3xl font-bold text-ek-purple" data-testid="readiness-pct">{s.readiness_pct}%</p>
              </div>
              <div className="rounded-xl border border-black/5 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-ek-ink/65">Implemented</p>
                <p className="font-display text-3xl font-bold text-green-700">{s.counts.implemented}</p>
              </div>
              <div className="rounded-xl border border-black/5 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-ek-ink/65">Partial</p>
                <p className="font-display text-3xl font-bold text-amber-600">{s.counts.partial}</p>
              </div>
              <div className="rounded-xl border border-black/5 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-ek-ink/65">Planned</p>
                <p className="font-display text-3xl font-bold text-slate-500">{s.counts.planned}</p>
              </div>
            </div>

            <Link to="/governance" data-testid="readiness-trust-centre-link" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-ek-purple hover:underline">
              <FileText size={15} /> Download the prep-pack PDFs in the Trust Centre <ArrowRight size={14} />
            </Link>

            {/* Control matrix */}
            <h2 className="mt-8 font-display text-lg font-semibold text-ek-ink">Control matrix</h2>
            <div className="mt-3 overflow-x-auto rounded-xl border border-black/5 bg-white" data-testid="control-matrix">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-black/5 text-xs uppercase tracking-wide text-ek-ink/65">
                  <tr>
                    <th className="px-3 py-2">ID</th><th className="px-3 py-2">Area</th>
                    <th className="px-3 py-2">SOC 2</th><th className="px-3 py-2">ISO Annex A</th>
                    <th className="px-3 py-2">Control &amp; ERIO implementation</th><th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-black/5">
                  {data.matrix.map((c) => (
                    <tr key={c.id} data-testid={`control-${c.id}`} className="align-top">
                      <td className="px-3 py-2 font-mono text-xs text-ek-ink/60">{c.id}</td>
                      <td className="px-3 py-2 font-medium text-ek-ink">{c.area}</td>
                      <td className="px-3 py-2 text-xs text-ek-ink/65">{c.soc2}</td>
                      <td className="px-3 py-2 text-xs text-ek-ink/65">{c.iso}</td>
                      <td className="px-3 py-2 text-ek-ink/70">
                        <span className="font-medium text-ek-ink">{c.control}</span>
                        <span className="mt-0.5 block text-xs text-ek-ink/65">{c.erio}</span>
                      </td>
                      <td className="px-3 py-2"><Pill s={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Gaps */}
            <h2 className="mt-8 font-display text-lg font-semibold text-ek-ink">Gap analysis &amp; remediation</h2>
            <div className="mt-3 space-y-2" data-testid="gap-list">
              {data.gaps.map((g) => (
                <div key={g.id} className="flex items-start gap-3 rounded-xl border border-black/5 bg-white p-4">
                  <Pill s={g.status} />
                  <div>
                    <p className="text-sm font-medium text-ek-ink">{g.area} <span className="font-mono text-xs text-ek-ink/65">({g.id})</span></p>
                    <p className="mt-0.5 text-sm text-ek-ink/60">{g.gap}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Policies */}
            <h2 className="mt-8 font-display text-lg font-semibold text-ek-ink">Policy set</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="policy-list">
              {data.policies.map((p) => (
                <div key={p.name} className="rounded-xl border border-black/5 bg-white p-4">
                  <p className="flex items-center gap-2 text-sm font-semibold text-ek-ink"><ShieldCheck size={15} className="text-ek-purple" /> {p.name}</p>
                  <p className="mt-1 text-sm text-ek-ink/65">{p.summary}</p>
                </div>
              ))}
            </div>

            <p className="mt-8 text-xs text-ek-ink/65">{data.disclaimer}</p>
          </>
        )}
      </main>
    </div>
  );
}
